/**
 * Debug utility for redirect command operations
 * Centralizes console logging with consistent formatting
 */

const DEBUG_PREFIX = "🔧 [CommandFlow]";

/**
 * Logs redirect command start information
 * @param {string} nextCommand - The command being redirected to
 * @param {Object} nextCommandData - Data for the redirect command
 * @param {Object} engine - The command engine
 */
export function logRedirectStart(nextCommand, nextCommandData, engine) {
  console.log("🚀 Starting redirect command:", nextCommand);
  console.log("🚀 Redirect command data:", nextCommandData);
  console.log("🚀 Engine available:", !!engine);
  console.log("🚀 Engine startCommand method:", typeof engine.startCommand);
  console.log("🚀 About to call engine.startCommand...");
}

/**
 * Logs redirect command result information
 * @param {Object} redirectResult - The result from the redirect command
 */
export function logRedirectResult(redirectResult) {
  console.log("🎯 Redirect command result:", redirectResult);
  console.log(
    "🎯 Redirect result structure:",
    JSON.stringify(redirectResult, null, 2)
  );
  console.log("🎯 Redirect result type:", typeof redirectResult);
  console.log(
    "🎯 Redirect result is null/undefined:",
    redirectResult === null || redirectResult === undefined
  );
}

/**
 * Logs redirect command error
 * @param {Error} error - The error that occurred
 */
export function logRedirectError(error) {
  console.error("❌ Error in redirect command execution:", error);
  console.error("❌ Error details:", error.message, error.stack);
}

/**
 * Logs redirect result processing start
 */
export function logRedirectProcessingStart() {
  console.log("🔍 Handling redirect result...");
}

/**
 * Logs redirect result analysis
 * @param {Object} redirectResult - The redirect result to analyze
 */
export function logRedirectResultAnalysis(redirectResult) {
  console.log("Redirect result has prompt:", !!redirectResult?.prompt);
  console.log("Redirect result has output:", !!redirectResult?.output);
}

/**
 * Logs when redirect command has no prompt
 */
export function logRedirectNoPrompt() {
  console.log("⚠️ Redirect command has no prompt, checking for output...");
}

/**
 * Logs when redirect command is handled as special output
 */
export function logRedirectSpecialOutput() {
  console.log("✅ Redirect command handled as special output");
}

/**
 * Logs when displaying redirect command output
 */
export function logRedirectDisplayOutput() {
  console.log("📝 Displaying redirect command output");
}

/**
 * Logs session state clearing
 */
export function logSessionStateClearing() {
  console.log("🧹 Clearing session state after redirect command completion");
}

/**
 * General purpose debug logger with context
 * @param {string} context - The context/module where the log is from
 * @param {string} message - The message to log
 * @param {any} data - Optional data to log
 */
export function debugLog(context, message, data = null) {
  const timestamp = new Date().toISOString().substr(11, 12);
  const prefix = `${DEBUG_PREFIX} [${timestamp}] [${context}]`;

  if (data !== null) {
    console.log(`${prefix} ${message}:`, data);
  } else {
    console.log(`${prefix} ${message}`);
  }
}

/**
 * Error logger with context
 * @param {string} context - The context/module where the error occurred
 * @param {string} message - The error message
 * @param {Error} error - The error object
 */
export function debugError(context, message, error) {
  const timestamp = new Date().toISOString().substr(11, 12);
  const prefix = `${DEBUG_PREFIX} [${timestamp}] [${context}] ❌`;
  console.error(`${prefix} ${message}:`, error);
  if (error.stack) {
    console.error(`${prefix} Stack trace:`, error.stack);
  }
}
